import * as THREE from 'three'
import type { DebugMaterialRegistry } from './debug-materials'

export class SchematicCameraController {
  private readonly scene: THREE.Scene
  private readonly perspectiveCamera: THREE.PerspectiveCamera
  private readonly orthoCamera: THREE.OrthographicCamera
  private activeCamera: THREE.Camera
  private isSchematic = false
  private readonly markerGroup: THREE.Group
  private readonly debugRegistry?: DebugMaterialRegistry

  constructor(
    scene: THREE.Scene,
    perspectiveCamera: THREE.PerspectiveCamera,
    width: number,
    height: number,
    debugRegistry?: DebugMaterialRegistry,
  ) {
    this.scene = scene
    this.perspectiveCamera = perspectiveCamera
    this.debugRegistry = debugRegistry

    const aspect = width / height
    const viewSize = 24
    this.orthoCamera = new THREE.OrthographicCamera(
      (-viewSize * aspect) / 2,
      (viewSize * aspect) / 2,
      viewSize / 2,
      -viewSize / 2,
      0.1,
      1000,
    )
    this.orthoCamera.position.set(-2, 25, 1)
    this.orthoCamera.lookAt(-2, 0, 1)
    this.orthoCamera.up.set(0, 0, -1) // North to screen top

    this.activeCamera = perspectiveCamera
    this.markerGroup = new THREE.Group()
    this.scene.add(this.markerGroup)

    this.buildSchematicGrid()
  }

  private buildSchematicGrid() {
    // 1. Orthographic Coordinate Grid Floor Marker
    const grid = new THREE.GridHelper(26, 26, 0x00f0ff, 0x223344)
    grid.position.set(-2, 0.05, 1)
    this.markerGroup.add(grid)

    // 2. Schematic Sector Bounding Outlines from Authentic ISO Coordinates
    const sectors = [
      { name: 'Spawn Chamber', min: [-2, -0.89], max: [2, 1.5], color: 0x00ff88 },
      { name: 'Launch Bay', min: [-2, -6.0], max: [2, -0.89], color: 0x00f0ff },
      { name: 'West Target Range', min: [-12, 1.5], max: [-2.07, 8.0], color: 0xffaa00 },
      { name: 'East Circuit Hub', min: [2.03, 1.5], max: [8.0, 8.0], color: 0x00ffff },
    ]

    for (const sec of sectors) {
      const w = sec.max[0] - sec.min[0]
      const d = sec.max[1] - sec.min[1]
      const cx = (sec.min[0] + sec.max[0]) / 2
      const cz = (sec.min[1] + sec.max[1]) / 2

      const boxGeo = new THREE.BoxGeometry(w, 0.08, d)
      const edges = new THREE.EdgesGeometry(boxGeo)
      const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: sec.color, linewidth: 2 }))
      line.position.set(cx, 0.08, cz)
      this.markerGroup.add(line)
    }

    this.markerGroup.visible = false
  }

  toggleSchematic(): boolean {
    return this.setSchematic(!this.isSchematic)
  }

  setSchematic(enable: boolean): boolean {
    this.isSchematic = enable
    this.activeCamera = this.isSchematic ? this.orthoCamera : this.perspectiveCamera
    this.markerGroup.visible = this.isSchematic

    // Hide ceilings in top-down schematic view to clearly expose interior walls and layout
    if (this.debugRegistry) {
      const ceilings = this.debugRegistry.getEntriesByType('ceiling')
      for (let i = 0; i < ceilings.length; i++) {
        ceilings[i].mesh.visible = !enable
      }
    }

    return this.isSchematic
  }

  getCamera(): THREE.Camera {
    return this.activeCamera
  }

  get isSchematicActive(): boolean {
    return this.isSchematic
  }

  handleResize(width: number, height: number) {
    const aspect = width / height
    const viewSize = 24
    this.orthoCamera.left = (-viewSize * aspect) / 2
    this.orthoCamera.right = (viewSize * aspect) / 2
    this.orthoCamera.top = viewSize / 2
    this.orthoCamera.bottom = -viewSize / 2
    this.orthoCamera.updateProjectionMatrix()
  }
}
